import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import FormPageLayout from '../../components/ui/FormPageLayout';
import FilterSelect from '../../components/ui/FilterSelect';
import { FORMA_LINK_BTN } from '../../utils/listPageUi';
import { formationAPI, spaEntityAPI } from '../../services/api';
import { fetchFormationEnrollmentBundle, resolveRoomLabel } from '../../utils/formationEnrollmentBundle';
import { invalidateFormationGraph } from '../../utils/invalidateFormationGraph';
import { SPA_CLASSROOMS_KEY, SPA_COURSES_KEY, FORMATION_ENROLLMENT_BUNDLE_KEY } from '../../utils/queryKeys';
import { checkTrainerBatchConflict } from '../../utils/trainerBatchConflict';
import { useLanguage } from '../../contexts/LanguageContext';
import { confirmDialog } from '../../utils/confirmBridge';
import notify from '../../utils/notify';
import {
  PlusIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ClockIcon,
  TrashIcon,
  PencilIcon,
  AcademicCapIcon,
  TagIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

const WEEKDAY_IDS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const BatchForm = ({
  courseId,
  courseName,
  onBatchCreated,
  onCancel,
  isEditing = false,
  batchData = null,
  classrooms = [],
  allBatchesForConflict = [],
}) => {
  const { t, language } = useLanguage();
  const dateLocale = language === 'ar' ? 'ar-DZ' : 'fr-FR';
  const [trainers, setTrainers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    batchName: batchData?.batchName || '',
    days: batchData?.days || [],
    startTime: batchData?.startTime || '',
    endTime: batchData?.endTime || '',
    startDate: batchData?.startDate || '',
    endDate: batchData?.endDate || '',
    trainerId: batchData?.trainerId || '',
    room: batchData?.room || '',
    maxCapacity: batchData?.maxCapacity || 15,
    courseId: courseId || batchData?.courseId || '',
    courseName: courseName || batchData?.courseName || ''
  });
  const [conflicts, setConflicts] = useState([]);

  // Load trainers / courses from D1 SpaEntities
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [trList, crsRaw] = await Promise.all([
          spaEntityAPI.list('trainers'),
          spaEntityAPI.list('courses'),
        ]);
        if (!cancelled) {
          setTrainers(
            (trList || []).map((t) => ({
              ...t,
              name: t.name || `${t.firstName || ''} ${t.lastName || ''}`.trim() || t.id,
            }))
          );
          setCourses(
            (crsRaw || []).map((c) => ({
              ...c,
              title: c.title || c.name || t('groupsPage.courseFallbackName'),
            }))
          );
        }
      } catch {
        if (!cancelled) {
          setTrainers([]);
          setCourses([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const weekDays = useMemo(
    () => WEEKDAY_IDS.map((id) => ({ id, name: t(`coursesPage.weekdays.${id}`) })),
    [t]
  );

  const handleDayToggle = (dayId) => {
    setFormData(prev => ({
      ...prev,
      days: prev.days.includes(dayId)
        ? prev.days.filter(d => d !== dayId)
        : [...prev.days, dayId]
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'courseId') {
      const selectedCourse = Array.isArray(courses) ? courses.find(c => c.id === value) : null;
      setFormData(prev => ({
        ...prev,
        [name]: value,
        courseName: selectedCourse?.title || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Check for conflicts when trainer, days, times, or dates change
  useEffect(() => {
    if (formData.trainerId && formData.days.length > 0 && formData.startTime && formData.endTime && formData.startDate && formData.endDate) {
      const conflictingBatches = checkTrainerBatchConflict(
        allBatchesForConflict,
        formData.trainerId,
        formData.days,
        formData.startTime,
        formData.endTime,
        formData.startDate,
        formData.endDate,
        isEditing ? batchData?.id : null
      );
      setConflicts(conflictingBatches);
    } else {
      setConflicts([]);
    }
  }, [
    formData.trainerId,
    formData.days,
    formData.startTime,
    formData.endTime,
    formData.startDate,
    formData.endDate,
    isEditing,
    batchData?.id,
    allBatchesForConflict,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (conflicts.length > 0) {
      notify.show(t('coursesPage.trainerConflictAlert'));
      return;
    }

    try {
      const selectedTrainer = Array.isArray(trainers)
        ? trainers.find((t) => t.id === formData.trainerId)
        : null;
      const payloadBase = {
        courseId: formData.courseId,
        courseName: formData.courseName,
        batchName: formData.batchName,
        days: formData.days,
        startTime: formData.startTime,
        endTime: formData.endTime,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        trainerId: formData.trainerId || null,
        trainerName: selectedTrainer?.name || t('coursesPage.unknownTrainer'),
        room: formData.room || null,
        maxCapacity: formData.maxCapacity ?? 15,
        status: 'active',
      };

      if (isEditing && batchData) {
        await formationAPI.updateBatch(batchData.id, { ...payloadBase, id: batchData.id });
        notify.show(t('groupsPage.batchSaveSuccessUpdated'));
      } else {
        const id = `batch-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
        await formationAPI.createBatch({ ...payloadBase, id });
        notify.show(t('groupsPage.batchSaveSuccessCreated'));
        setFormData({
          batchName: '',
          days: [],
          startTime: '',
          endTime: '',
          startDate: '',
          endDate: '',
          trainerId: '',
          room: '',
          maxCapacity: 15,
          courseId: '',
          courseName: '',
        });
      }
      onBatchCreated?.();
    } catch (error) {
      console.error('Error saving batch:', error);
      notify.show(t('groupsPage.batchSaveError'));
    }
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">
        {isEditing ? t('coursesPage.batchFormTitleEdit') : t('coursesPage.batchFormTitleNew')}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Course Selection */}
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblCourseShort')}
            </label>
            <select
              name="courseId"
              value={formData.courseId}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t('coursesPage.batchSelectCourse')}</option>
              {Array.isArray(courses) && courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>

          {/* Group Name */}
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblGroupName')}
            </label>
            <input
              type="text"
              name="batchName"
              value={formData.batchName}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('coursesPage.batchGroupNamePlaceholder')}
              required
            />
          </div>

          {/* Days Selection */}
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblDays')}
            </label>
            <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
              {weekDays.map(day => (
                <button
                  key={day.id}
                  type="button"
                  onClick={() => handleDayToggle(day.id)}
                  className={`p-2 rounded-lg text-sm font-medium transition-all ${
                    formData.days.includes(day.id)
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100/80 dark:bg-slate-800/40 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/80'
                  }`}
                >
                  {day.name}
                </button>
              ))}
            </div>
          </div>

          {/* Time Range */}
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblStartTime')}
            </label>
            <input
              type="time"
              name="startTime"
              value={formData.startTime}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblEndTime')}
            </label>
            <input
              type="time"
              name="endTime"
              value={formData.endTime}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblStartDateShort')} ({t('coursesPage.optionalSuffix')})
            </label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblEndDateShort')} ({t('coursesPage.optionalSuffix')})
            </label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Trainer */}
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblTrainerForm')}
            </label>
            <select
              name="trainerId"
              value={formData.trainerId}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t('coursesPage.batchSelectTrainer')}</option>
              {Array.isArray(trainers) && trainers.map(trainer => (
                <option key={trainer.id} value={trainer.id}>
                  {trainer.name}
                </option>
              ))}
            </select>
          </div>

          {/* Capacity */}
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblMaxCapacity')}
            </label>
            <input
              type="number"
              name="maxCapacity"
              value={formData.maxCapacity}
              onChange={handleChange}
              min="1"
              max="50"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Room */}
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('coursesPage.batchLblRoomOptional')}
            </label>
            <select
              name="room"
              value={formData.room}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('coursesPage.batchSelectRoom')}</option>
              {Array.isArray(classrooms) && classrooms.map(classroom => (
                <option key={classroom.id} value={classroom.id}>
                  {t('coursesPage.roomPickMetaLine', {
                    name: classroom.name,
                    code: classroom.code,
                    capacity: classroom.capacity,
                    occupied: !classroom.isAvailable ? t('coursesPage.batchRoomOccupiedSuffix').trim() : '',
                  })}
                </option>
              ))}
            </select>
            
            {/* Show selected room details */}
            {formData.room && (() => {
              const selectedRoom = classrooms.find(room => room.id === formData.room || room.name === formData.room);
              return selectedRoom ? (
                <div className="mt-2 rounded-lg border border-forma-200 bg-forma-50 p-3 dark:border-forma-800/50 dark:bg-forma-950/30">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="text-sm font-semibold text-forma-900 dark:text-forma-100">
                        {selectedRoom.name} ({selectedRoom.code})
                      </h4>
                      <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                        {t('coursesPage.roomDetailsCapacityFloor', {
                          cap: selectedRoom.capacity,
                          floor: selectedRoom.floor || t('coursesPage.floorNA'),
                        })}
                      </p>
                    </div>
                    <span
                      className={clsx(
                        'shrink-0 rounded-full px-2 py-1 text-xs font-medium',
                        selectedRoom.isAvailable
                          ? 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-400'
                      )}
                    >
                      {selectedRoom.isAvailable ? t('classroomsPage.available') : t('classroomsPage.occupied')}
                    </span>
                  </div>

                  {/* Equipment icons */}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedRoom.hasProjector && (
                      <span className="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600">
                        📽️ {t('classroomsPage.projector')}
                      </span>
                    )}
                    {selectedRoom.hasAirConditioning && (
                      <span className="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600">
                        ❄️ {t('classroomsPage.airConditioning')}
                      </span>
                    )}
                    {selectedRoom.hasComputers && (
                      <span className="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600">
                        {t('coursesPage.equipmentPcShort')}
                      </span>
                    )}
                    {selectedRoom.hasWhiteboard && (
                      <span className="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600">
                        📋 {t('classroomsPage.whiteboard')}
                      </span>
                    )}
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        </div>

        {/* Enhanced Conflict warnings */}
        {conflicts.length > 0 && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mr-2" />
              <div>
                <span className="text-red-400 font-medium text-lg">{t('coursesPage.conflictDetectedTitle')}</span>
                <p className="text-red-300 text-xs mt-1">{t('coursesPage.conflictDetectedSub')}</p>
              </div>
            </div>
            
            <div className="bg-red-500/10 rounded-lg p-3 mb-3">
              <h4 className="text-red-300 font-medium text-sm mb-2">{t('coursesPage.conflictingCoursesHdr')}</h4>
              <div className="space-y-2">
                {conflicts.map(conflict => (
                  <div key={conflict.id} className="bg-red-500/20 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-red-200 font-medium">{conflict.courseName}</div>
                        <div className="text-red-300 text-xs">{conflict.batchName}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-red-200 text-sm">
                          {conflict.days.map((d) => t(`coursesPage.weekdays.${d}`) || d).join(', ')}
                        </div>
                        <div className="text-red-300 text-xs">{conflict.startTime} - {conflict.endTime}</div>
                      </div>
                    </div>
                    {conflict.room && (
                      <div className="text-red-300 text-xs mt-1">{t('coursesPage.conflictRoomLbl')} {conflict.room}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
              <h4 className="text-yellow-300 font-medium text-sm mb-2">{t('coursesPage.suggestTitle')}</h4>
              <ul className="text-yellow-200 text-xs space-y-1">
                <li>{t('coursesPage.suggestLine1')}</li>
                <li>{t('coursesPage.suggestLine2')}</li>
                <li>{t('coursesPage.suggestLine3')}</li>
                <li>{t('coursesPage.suggestLine4')}</li>
              </ul>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            {t('coursesPage.cancelBtnShort')}
          </button>
          <button
            type="submit"
            disabled={conflicts.length > 0}
            className={clsx(
              FORMA_LINK_BTN,
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {isEditing ? t('coursesPage.batchSubmitEdit') : t('coursesPage.batchSubmitCreate')}
          </button>
        </div>
      </form>
    </div>
  );
};

const GroupCard = ({ batch, classrooms = [], onEdit, onDelete, onViewStudents }) => {
  const { t, language } = useLanguage();
  const dateLocale = language === 'ar' ? 'ar-DZ' : 'fr-FR';

  const translatedDays = Array.isArray(batch.days)
    ? batch.days.map((day) => t(`coursesPage.weekdays.${day}`) || day).join(', ')
    : t('groupsPage.daysNotDefined');

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800/80">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h4 className="truncate font-medium text-slate-900 dark:text-slate-50">
            {batch.batchName || t('groupsPage.nameNotDefined')}
          </h4>
          <p className="truncate text-sm text-slate-600 dark:text-slate-400">
            {batch.courseName || t('groupsPage.courseNotDefinedShort')}
          </p>
          <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
            {translatedDays} • {batch.startTime || '\u2014'} - {batch.endTime || '\u2014'}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 self-start">
          <span
            className={clsx(
              'rounded-full px-2 py-1 text-xs font-medium tabular-nums',
              batch.isFull
                ? 'bg-red-500/20 text-red-400'
                : 'bg-green-500/20 text-green-400'
            )}
          >
            {batch.currentEnrollment || 0}/{batch.maxCapacity || '\u2014'}
          </span>
          <button
            type="button"
            onClick={() => onViewStudents(batch)}
            className="rounded p-1.5 bg-blue-500/20 text-blue-400 transition-colors hover:bg-blue-500/30"
            title={t('commonActions.viewStudents')}
          >
            <UserGroupIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onEdit(batch)}
            className="rounded p-1.5 bg-yellow-500/20 text-yellow-400 transition-colors hover:bg-yellow-500/30"
            title={t('commonActions.editGroup')}
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(batch.id)}
            className="rounded p-1.5 bg-red-500/20 text-red-400 transition-colors hover:bg-red-500/30"
            title={t('commonActions.delete')}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-xs text-slate-500 dark:text-slate-400">{t('coursesPage.lblTrainerColon')}</dt>
          <dd className="mt-0.5 truncate text-sm font-medium text-slate-900 dark:text-slate-50">
            {batch.trainerName || t('groupsPage.trainerNotAssigned')}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-slate-500 dark:text-slate-400">{t('coursesPage.lblRoomColon')}</dt>
          <dd className="mt-0.5 truncate text-sm font-medium text-slate-900 dark:text-slate-50">
            {resolveRoomLabel(classrooms, batch.room, t('coursesPage.roomUndefined'))}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-slate-500 dark:text-slate-400">{t('coursesPage.lblStartColon')}</dt>
          <dd className="mt-0.5 text-sm font-medium tabular-nums text-slate-900 dark:text-slate-50">
            {batch.startDate
              ? new Date(batch.startDate).toLocaleDateString(dateLocale)
              : t('coursesPage.roomUndefined')}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-slate-500 dark:text-slate-400">{t('coursesPage.lblEndColon')}</dt>
          <dd className="mt-0.5 text-sm font-medium tabular-nums text-slate-900 dark:text-slate-50">
            {batch.endDate
              ? new Date(batch.endDate).toLocaleDateString(dateLocale)
              : t('coursesPage.roomUndefined')}
          </dd>
        </div>
      </dl>

      {/* Enrollment progress bar */}
      <div className="mt-4 border-t border-slate-200 pt-3 dark:border-slate-600">
        <div className="mb-1 flex justify-between text-xs text-slate-600 dark:text-slate-400">
          <span>{t('groupsPage.enrollmentShort')}</span>
          <span className="tabular-nums">
            {batch.maxCapacity > 0
              ? (((batch.currentEnrollment || 0) / batch.maxCapacity) * 100).toFixed(0)
              : 0}
            %
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800/80">
          <div
            className="h-full rounded-full bg-gradient-to-r from-forma-500 to-forma-700 transition-all duration-300"
            style={{
              width: `${
                batch.maxCapacity > 0
                  ? ((batch.currentEnrollment || 0) / batch.maxCapacity) * 100
                  : 0
              }%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

const GroupsListView = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();
  const dateLocale = language === 'ar' ? 'ar-DZ' : 'fr-FR';
  const [selectedBatchStudents, setSelectedBatchStudents] = useState(null);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [filterCourse, setFilterCourse] = useState('');

  const { data: bundleData, isLoading, refetch } = useQuery(
    FORMATION_ENROLLMENT_BUNDLE_KEY,
    fetchFormationEnrollmentBundle,
    {
      staleTime: 4000,
      refetchInterval: 8000,
      refetchOnWindowFocus: true,
    }
  );

  const enrollmentsCatalog = bundleData?.enrollments || [];

  const { data: studentsCatalogItems = [] } = useQuery(
    ['formation', 'students', 'catalog-lite'],
    async () => (await formationAPI.getStudents({ limit: 500, page: 1 })).items || [],
    { staleTime: 30_000 }
  );

  const { data: coursesData } = useQuery(SPA_COURSES_KEY, () =>
    spaEntityAPI.list('courses').then((crs) =>
      (crs || []).map((c) => ({ ...c, title: c.title || c.name || t('groupsPage.courseFallbackName') }))
    )
  );

  const { data: classroomsData } = useQuery(SPA_CLASSROOMS_KEY, () =>
    spaEntityAPI.list('classrooms')
  );

  const batches = bundleData?.batches || [];  const courses = Array.isArray(coursesData) ? coursesData : [];
  const classrooms = Array.isArray(classroomsData) ? classroomsData : [];

  // Enhance batches with fresh course names
  const batchesWithCorrectCourseNames = batches.map(batch => {
    const correspondingCourse = courses.find(course => course.id === batch.courseId);
    return {
      ...batch,
      courseName: correspondingCourse?.title || batch.courseName || t('groupsPage.courseNotFoundShort')
    };
  });

  // Filter batches by course using enhanced data
  const filteredBatches = filterCourse 
    ? batchesWithCorrectCourseNames.filter(batch => batch.courseId === filterCourse)
    : batchesWithCorrectCourseNames;

  // Group batches by course using enhanced data
  const batchesByCourse = filteredBatches.reduce((acc, batch) => {
    const courseId = batch.courseId;
    if (!acc[courseId]) {
      acc[courseId] = [];
    }
    acc[courseId].push(batch);
    return acc;
  }, {});

  const formaPrimaryBtn = FORMA_LINK_BTN;

  const courseFilterTabs = useMemo(
    () => [{ id: '', name: t('coursesPage.tabAll') }, ...courses.map((c) => ({ id: c.id, name: c.title }))],
    [courses, t]
  );

  const courseTabCounts = useMemo(() => {
    const counts = { '': batchesWithCorrectCourseNames.length };
    courses.forEach((c) => {
      counts[c.id] = batchesWithCorrectCourseNames.filter((b) => b.courseId === c.id).length;
    });
    return counts;
  }, [batchesWithCorrectCourseNames, courses]);

  const groupsStats = useMemo(
    () => [
      {
        title: t('groupsPage.statsTotalGroupsTitle'),
        value: batchesWithCorrectCourseNames.length.toString(),
        hint: t('groupsPage.statsAllFormationsHint'),
        icon: TagIcon,
        iconWrap:
          'bg-forma-100 text-forma-700 shadow-inner dark:bg-forma-950/50 dark:text-forma-400',
      },
      {
        title: t('groupsPage.statsCoursesWithGroupsTitle'),
        value: new Set(batchesWithCorrectCourseNames.map((b) => b.courseId)).size.toString(),
        hint: t('groupsPage.statsCoursesWithGroupsHint'),
        icon: AcademicCapIcon,
        iconWrap:
          'bg-emerald-100 text-emerald-800 shadow-inner dark:bg-emerald-950/45 dark:text-emerald-400',
      },
      {
        title: t('groupsPage.statCumulativeCapacityTitle'),
        value: batchesWithCorrectCourseNames
          .reduce((s, b) => s + (Number(b.maxCapacity) || 0), 0)
          .toString(),
        hint: t('groupsPage.statCumulativeCapacityHint'),
        icon: UserGroupIcon,
        iconWrap: 'bg-rose-100 text-rose-800 shadow-inner dark:bg-rose-950/40 dark:text-rose-400',
      },
    ],
    [batchesWithCorrectCourseNames, t]
  );

  const handleEditBatch = (batch) => {
    navigate(`/groups/edit/${batch.id}`);
  };

  const handleDeleteBatch = async (batchId) => {
    if (!(await confirmDialog(t('groupsPage.confirmDeleteGroup')))) return;
    try {
      await formationAPI.deleteBatch(batchId);
      refetch();
      await invalidateFormationGraph(queryClient);
    } catch (e) {
      console.error(e);
      notify.show(t('groupsPage.deleteGroupError'));
    }
  };

  const handleViewStudents = (batch) => {
    const enrollments = enrollmentsCatalog.filter((e) => e.batchId === batch.id && e.status === 'active');
    const byId = Object.fromEntries(
      (studentsCatalogItems || []).filter((s) => s?.id).map((s) => [s.id, s])
    );
    const students = enrollments
      .map((enrollment) => {
        const student = byId[enrollment.studentId];
        return student
          ? {
              ...student,
              enrollmentDate: enrollment.enrollmentDate,
              enrollmentId: enrollment.id,
            }
          : null;
      })
      .filter(Boolean);

    setSelectedBatchStudents({
      batch,
      students,
      enrollmentCount: enrollments.length,
    });
    setShowStudentsModal(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('groupsPageTitle')}
        description={t('pageDescriptions.groups')}
        actions={
          <Link to="/groups/add" className={formaPrimaryBtn}>
            <PlusIcon className="h-5 w-5" />
            <span>{t('groupsPage.newGroup')}</span>
          </Link>
        }
      />

      <UiCard padding={false} className="overflow-hidden">
        <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
          <div className="grid gap-6 p-6 sm:grid-cols-3 sm:gap-5">
            {groupsStats.map((stat, index) => (
              <motion.div
                key={stat.title}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.06 }}
                whileHover={{ y: -3 }}
                className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
              >
                <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                  />
                  <div className="relative flex gap-4">
                    <div
                      className={clsx(
                        'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                        stat.iconWrap
                      )}
                    >
                      <stat.icon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        {stat.title}
                      </p>
                      <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                        {stat.value}
                      </p>
                      <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                        {stat.hint}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="border-b border-slate-200 bg-white/40 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/35">
          <FilterSelect
            id="group-course-filter"
            label={t('groupsPage.filterCourseChipLbl')}
            value={filterCourse || 'all'}
            onChange={(v) => setFilterCourse(v === 'all' ? '' : v)}
            options={courseFilterTabs.map((tab) => ({
              id: tab.id || 'all',
              name: tab.name,
              count: courseTabCounts[tab.id] ?? 0,
            }))}
            className="max-w-xs"
          />
        </div>
      </UiCard>

        {/* Groups List */}
        <div className="space-y-6">
          {isLoading ? (
            <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">{t('groupsPage.loadingList')}</p>
            </div>
          ) : filteredBatches.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-12 text-center">
              <TagIcon className="h-16 w-16 text-slate-900 dark:text-slate-50/30 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2">{t('groupsPage.emptyTitleNoMatch')}</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {filterCourse ? t('groupsPage.emptyBodyFiltered') : t('groupsPage.emptyBodyCreateFirst')}
              </p>
              <Link
                to="/groups/add"
                className={`${formaPrimaryBtn} mx-auto flex items-center justify-center gap-2`}
              >
                <PlusIcon className="h-5 w-5" />
                <span>{t('groupsPage.createFirstGroup')}</span>
              </Link>
            </div>
          ) : (
            Object.entries(batchesByCourse).map(([courseId, courseBatches]) => {
              const course = Array.isArray(courses) ? courses.find(c => c.id === courseId) : null;
              return (
                <div key={courseId} className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="rounded-lg bg-forma-500/15 p-2 ring-1 ring-forma-500/25">
                        <AcademicCapIcon className="h-6 w-6 text-forma-600 dark:text-forma-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">
                          {course?.title || t('groupsPage.unknownCourseTitle')}
                        </h3>
                        <p className="text-slate-600 dark:text-slate-400 text-sm">
                          {t('groupsPage.groupsPerCourseSuffix', { n: courseBatches.length })}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {courseBatches.map(batch => (
                      <GroupCard
                        key={batch.id}
                        batch={batch}
                        classrooms={classrooms}
                        onEdit={handleEditBatch}
                        onDelete={handleDeleteBatch}
                        onViewStudents={handleViewStudents}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Students Modal */}
        {showStudentsModal && selectedBatchStudents && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="max-h-[80vh] max-w-4xl w-full overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-soft dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
                    {t('groupsPage.modalStudentsForBatchTitle', {
                      name: selectedBatchStudents.batch.batchName,
                    })}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">
                    {selectedBatchStudents.batch.courseName} •{' '}
                    {(selectedBatchStudents.batch.days || [])
                      .map((d) => t(`coursesPage.weekdays.${d}`) || d)
                      .join(', ')}{' '}
                    • {selectedBatchStudents.batch.startTime} -{' '}
                    {selectedBatchStudents.batch.endTime}
                  </p>
                </div>
                <button
                  onClick={() => setShowStudentsModal(false)}
                  className="p-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-700 dark:text-slate-300">
                    {t('groupsPage.modalTotalStudentCount', {
                      n: selectedBatchStudents.students.length,
                    })}
                  </span>
                  <span className="text-slate-600 dark:text-slate-400 text-sm">
                    {t('groupsPage.modalCapacityLabelShort')}:{' '}
                    {selectedBatchStudents.students.length}/{selectedBatchStudents.batch.maxCapacity}
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-50 dark:bg-slate-800/80 rounded-full mt-2 overflow-hidden">
                  <div 
                    className="h-full rounded-full bg-gradient-to-r from-forma-500 to-forma-700 transition-all duration-300" 
                    style={{ width: `${(selectedBatchStudents.students.length / selectedBatchStudents.batch.maxCapacity) * 100}%` }}
                  ></div>
                </div>
              </div>

              <div className="overflow-y-auto max-h-96">
                {selectedBatchStudents.students.length === 0 ? (
                  <div className="text-center py-12">
                    <UserGroupIcon className="h-16 w-16 text-slate-900 dark:text-slate-50/30 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2">
                      {t('groupsPage.noStudentsInBatchTitle')}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400">
                      {t('groupsPage.noStudentsInBatchBody')}
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {selectedBatchStudents.students.map((student) => (
                      <div key={student.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-forma-500/15 ring-1 ring-forma-500/20">
                              <UserGroupIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
                            </div>
                            <div>
                              <h4 className="text-slate-900 dark:text-slate-50 font-medium">{student.name}</h4>
                              <p className="text-slate-600 dark:text-slate-400 text-sm">{student.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-slate-600 dark:text-slate-400 text-sm">
                              {t('groupsPage.enrolledOnPrefix')}{' '}
                              {new Date(student.enrollmentDate).toLocaleDateString(dateLocale)}
                            </div>
                            <div className="text-slate-600 dark:text-slate-400 text-xs">
                              ID: {student.studentCode || student.id}
                            </div>
                          </div>
                        </div>
                        
                        {student.phone && (
                          <div className="mt-2 text-slate-600 dark:text-slate-400 text-sm">
                            📞 {student.phone}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6 pt-4 border-t border-slate-200 dark:border-slate-600">
                <button
                  onClick={() => setShowStudentsModal(false)}
                  className={`${formaPrimaryBtn} px-6 py-2`}
                >
                  {t('groupsPage.studentsModalClose')}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

const AddGroupView = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: bundleData } = useQuery(
    FORMATION_ENROLLMENT_BUNDLE_KEY,
    fetchFormationEnrollmentBundle
  );
  const { data: classroomsData } = useQuery(SPA_CLASSROOMS_KEY, () =>
    spaEntityAPI.list('classrooms')
  );
  const classrooms = Array.isArray(classroomsData) ? classroomsData : [];
  const batches = bundleData?.batches || [];

  const handleBatchCreated = async () => {
    await invalidateFormationGraph(queryClient);
    navigate('/groups');
  };

  return (
    <FormPageLayout backTo="/groups" backLabel={t('groupsPage.backToList')}>
      <BatchForm
        onBatchCreated={handleBatchCreated}
        onCancel={() => navigate('/groups')}
        classrooms={classrooms}
        allBatchesForConflict={batches}
      />
    </FormPageLayout>
  );
};

const EditGroupView = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const { data: bundleData, isLoading } = useQuery(
    FORMATION_ENROLLMENT_BUNDLE_KEY,
    fetchFormationEnrollmentBundle
  );
  const { data: classroomsData } = useQuery(SPA_CLASSROOMS_KEY, () =>
    spaEntityAPI.list('classrooms')
  );

  const batch = (bundleData?.batches || []).find((b) => b.id === id);
  const classrooms = Array.isArray(classroomsData) ? classroomsData : [];
  const batches = bundleData?.batches || [];

  const handleBatchCreated = async () => {
    await invalidateFormationGraph(queryClient);
    navigate('/groups');
  };

  if (isLoading) {
    return (
      <FormPageLayout backTo="/groups" backLabel={t('groupsPage.backToList')}>
        <p className="text-slate-600 dark:text-slate-400">{t('groupsPage.loadingList')}</p>
      </FormPageLayout>
    );
  }

  if (!batch) {
    return (
      <FormPageLayout backTo="/groups" backLabel={t('groupsPage.backToList')}>
        <p className="text-slate-700 dark:text-slate-300">{t('groupsPage.emptyTitleNoMatch')}</p>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout backTo="/groups" backLabel={t('groupsPage.backToList')}>
      <BatchForm
        isEditing
        batchData={batch}
        onBatchCreated={handleBatchCreated}
        onCancel={() => navigate('/groups')}
        classrooms={classrooms}
        allBatchesForConflict={batches}
      />
    </FormPageLayout>
  );
};

const GroupsPage = () => (
  <Routes>
    <Route index element={<GroupsListView />} />
    <Route path="add" element={<AddGroupView />} />
    <Route path="edit/:id" element={<EditGroupView />} />
  </Routes>
);

export default GroupsPage;

